import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Search, X, Plus, ExternalLink, Pencil, Trash2, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useUrlState } from "@/hooks/use-url-state";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { ComplianceLayout } from "./compliance-layout";
import { ComplianceDocumentModal } from "@/components/modals/compliance-document-modal";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ComplianceDocument {
  id: string;
  organisationId: string | null;
  documentCategory: string;
  documentName: string;
  documentDescription: string | null;
  version: string | null;
  effectiveDate: string | null;
  reviewDate: string | null;
  documentStatus: string;
  fileUrl: string | null;
  filePath: string | null;
  approvedBy: string | null;
  approvedDate: string | null;
  regulatoryReference: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CATEGORIES = [
  { value: "all", label: "All" },
  { value: "policy", label: "Policy" },
  { value: "procedure", label: "Procedure" },
  { value: "template", label: "Template" },
  { value: "register", label: "Register" },
  { value: "report", label: "Report" },
  { value: "certificate", label: "Certificate" },
  { value: "other", label: "Other" },
];

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  draft: {
    label: "Draft",
    className: "bg-slate-100 text-slate-700 border-slate-300",
  },
  active: {
    label: "Active",
    className: "bg-green-100 text-green-700 border-green-300",
  },
  superseded: {
    label: "Superseded",
    className: "bg-amber-100 text-amber-700 border-amber-300",
  },
  archived: {
    label: "Archived",
    className: "bg-gray-100 text-gray-500 border-gray-300",
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatDate = (value: string | null | undefined): string => {
  if (!value) return "-";
  try {
    return format(new Date(value), "dd/MM/yyyy");
  } catch {
    return value;
  }
};

// ---------------------------------------------------------------------------
// Status Badge
// ---------------------------------------------------------------------------

interface StatusBadgeProps {
  status: string;
}

function StatusBadge({ status }: StatusBadgeProps) {
  const config = STATUS_CONFIG[status] ?? {
    label: status,
    className: "bg-gray-100 text-gray-700 border-gray-300",
  };
  return (
    <Badge
      variant="outline"
      className={config.className}
      data-testid={`status-badge-${status}`}
    >
      {config.label}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

function DocumentLibraryContent() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [activeCategory, setActiveCategory] = useState("all");
  const [search, setSearch] = useUrlState("search", "", { debounceMs: 300 });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDocument, setEditingDocument] =
    useState<ComplianceDocument | null>(null);
  const [deletingDocument, setDeletingDocument] =
    useState<ComplianceDocument | null>(null);

  // Build query params
  const queryParams = new URLSearchParams();
  if (search) queryParams.set("search", search);
  if (activeCategory !== "all") queryParams.set("category", activeCategory);

  const { data, isLoading } = useQuery<ComplianceDocument[]>({
    queryKey: ["/api/compliance/documents", search, activeCategory],
    queryFn: async () => {
      const url = `/api/compliance/documents${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;
      const response = await apiRequest("GET", url);
      return response.json();
    },
  });

  // Fetch all docs for category counts (unfiltered by category)
  const { data: allDocs } = useQuery<ComplianceDocument[]>({
    queryKey: ["/api/compliance/documents", "all-counts"],
    queryFn: async () => {
      const url = search
        ? `/api/compliance/documents?search=${encodeURIComponent(search)}`
        : "/api/compliance/documents";
      const response = await apiRequest("GET", url);
      return response.json();
    },
  });

  const documents = Array.isArray(data) ? data : [];
  const allDocuments = Array.isArray(allDocs) ? allDocs : [];

  // Category counts from allDocuments (respects search but not category filter)
  const categoryCounts = CATEGORIES.reduce<Record<string, number>>(
    (acc, cat) => {
      if (cat.value === "all") {
        acc[cat.value] = allDocuments.length;
      } else {
        acc[cat.value] = allDocuments.filter(
          (d) => d.documentCategory === cat.value
        ).length;
      }
      return acc;
    },
    {}
  );

  const clearSearch = () => setSearch("");

  // ---------------------------------------------------------------------------
  // Delete mutation
  // ---------------------------------------------------------------------------

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest(
        "DELETE",
        `/api/compliance/documents/${id}`
      );
      if (!response.ok && response.status !== 204) {
        const err = await response.json().catch(() => ({}));
        throw new Error(
          (err as { error?: string }).error ?? "Failed to delete document"
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/compliance/documents"],
      });
      toast({ title: "Document removed" });
      setDeletingDocument(null);
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const handleDelete = () => {
    if (deletingDocument) {
      deleteMutation.mutate(deletingDocument.id);
    }
  };

  const handleEdit = (doc: ComplianceDocument) => {
    setEditingDocument(doc);
    setIsModalOpen(true);
  };

  const handleModalClose = (open: boolean) => {
    setIsModalOpen(open);
    if (!open) {
      setEditingDocument(null);
    }
  };

  const handleViewDocument = (doc: ComplianceDocument) => {
    if (doc.fileUrl) {
      window.open(doc.fileUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Document Library</h1>
          <p className="text-sm text-muted-foreground">
            Compliance policies, procedures, templates and regulatory documents
          </p>
        </div>
        <Button
          data-testid="button-upload-doc"
          onClick={() => {
            setEditingDocument(null);
            setIsModalOpen(true);
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Upload Document
        </Button>
      </div>

      <div className="flex gap-4">
        {/* Category sidebar */}
        <aside className="w-48 shrink-0">
          <Card className="p-2">
            <nav className="space-y-0.5" aria-label="Document categories">
              {CATEGORIES.map((cat) => {
                const count = categoryCounts[cat.value] ?? 0;
                const isActive = activeCategory === cat.value;
                return (
                  <button
                    key={cat.value}
                    type="button"
                    data-testid={`category-${cat.value}`}
                    data-active={isActive ? "true" : "false"}
                    onClick={() => setActiveCategory(cat.value)}
                    className={cn(
                      "w-full flex items-center justify-between rounded-md px-3 py-2 text-sm transition-colors",
                      isActive
                        ? "bg-primary text-primary-foreground font-medium"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <span>{cat.label}</span>
                    <span
                      className={cn(
                        "rounded-full px-1.5 py-0.5 text-xs font-medium",
                        isActive
                          ? "bg-primary-foreground/20 text-primary-foreground"
                          : "bg-muted text-muted-foreground"
                      )}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </nav>
          </Card>
        </aside>

        {/* Main content */}
        <div className="flex-1 min-w-0 space-y-3">
          {/* Search bar */}
          <div className="flex items-center gap-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name or description..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-9"
                data-testid="input-search-documents"
              />
              {search && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label="Clear search"
                  data-testid="button-clear-search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {search && (
              <span className="text-sm text-muted-foreground">
                {documents.length} result{documents.length !== 1 ? "s" : ""}{" "}
                for &ldquo;{search}&rdquo;
              </span>
            )}
          </div>

          {/* Documents table */}
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Effective Date</TableHead>
                  <TableHead>Review Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 7 }).map((__, j) => (
                        <TableCell key={j}>
                          <div className="h-4 w-full animate-pulse rounded bg-muted" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : documents.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="py-16 text-center text-muted-foreground"
                    >
                      <div className="flex flex-col items-center gap-3">
                        <FileText className="h-10 w-10 text-muted-foreground/40" />
                        <p>No compliance documents found</p>
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid="button-upload-doc-empty"
                          onClick={() => {
                            setEditingDocument(null);
                            setIsModalOpen(true);
                          }}
                        >
                          <Plus className="mr-1 h-4 w-4" />
                          Upload Document
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  documents.map((doc) => (
                    <TableRow
                      key={doc.id}
                      data-testid={`row-document-${doc.id}`}
                    >
                      <TableCell className="font-medium max-w-xs">
                        <span
                          className="truncate block"
                          title={doc.documentName}
                        >
                          {doc.documentName}
                        </span>
                        {doc.documentDescription && (
                          <span className="text-xs text-muted-foreground truncate block">
                            {doc.documentDescription}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="capitalize">
                        {doc.documentCategory}
                      </TableCell>
                      <TableCell className="text-sm">
                        {doc.version ?? "-"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        {formatDate(doc.effectiveDate)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        {formatDate(doc.reviewDate)}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={doc.documentStatus} />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          {doc.fileUrl && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              aria-label="View document"
                              data-testid={`button-view-${doc.id}`}
                              onClick={() => handleViewDocument(doc)}
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            aria-label="Edit document"
                            data-testid={`button-edit-${doc.id}`}
                            onClick={() => handleEdit(doc)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive hover:text-destructive"
                            aria-label="Delete document"
                            data-testid={`button-delete-${doc.id}`}
                            onClick={() => setDeletingDocument(doc)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>
        </div>
      </div>

      {/* Upload / Edit modal */}
      <ComplianceDocumentModal
        open={isModalOpen}
        onOpenChange={handleModalClose}
        editingDocument={editingDocument}
      />

      {/* Delete confirmation dialog */}
      <AlertDialog
        open={!!deletingDocument}
        onOpenChange={(open) => {
          if (!open) setDeletingDocument(null);
        }}
      >
        <AlertDialogContent data-testid="dialog-delete-document">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete &ldquo;{deletingDocument?.documentName}&rdquo;?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the document record. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-confirm-delete"
            >
              {deleteMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function DocumentLibraryPage() {
  return (
    <ComplianceLayout>
      <DocumentLibraryContent />
    </ComplianceLayout>
  );
}
